/**
 * oleFrameConverter — `OLE2FRAME` → CadPrimitive[] (@codex PR #785 round 2, F1).
 *
 * # What is plotted, and what deliberately is not
 *
 * PLOTTED: the frame RECTANGLE between the entity's two stored WCS corners,
 * stroked in the entity's resolved plot colour / lineweight / linetype.
 *
 * NOT PLOTTED: the embedded OLE payload. It is an OLE compound document whose
 * stream is, for the common "Picture (Enhanced Metafile)" case, an EMF;
 * rasterising EMF needs a metafile interpreter this project does not have, so
 * the picture is not drawn ANYWHERE in this project (see `oleFrameEntitySchema`
 * and `scene3d/adapters/typed/oleFrameFromEntity.ts`) and the plot keeps that
 * boundary rather than inventing a placeholder. The frame alone is also what
 * AutoCAD itself displays when the OLE server is unavailable — the documented
 * degraded behaviour of the real application.
 *
 * # Why the FOUR corners are mapped, not the two stored ones
 *
 * `OLE2FRAME` stores only two OPPOSITE corners and carries no rotation, so the
 * rectangle is implied. Under an oblique block rotation the plotted frame is a
 * ROTATED rectangle: mapping the two stored corners and re-expanding them would
 * emit the AXIS-ALIGNED box around the diagonal — a different quadrilateral
 * from the one on screen. So the four corners are built FIRST (by the one
 * shared {@link oleFrameCorners} the render arm also consumes, so the plot and
 * the screen cannot disagree about which four points the frame is) and each is
 * mapped through the affine.
 */

import type { OleFrameEntity } from "@dxf-ai-editor/drawing-model";

import { oleFrameCorners } from "../../../scene3d/adapters/typed/oleFrameFromEntity";
import type { CadConvertContext, CadPrimitive, Point2 } from "../cadPrimitive";
import { apply, IDENTITY, type Affine2D } from "./affine2d";
import { strokedPathPrimitives } from "./strokedPathPrimitive";

/**
 * The frame's four corners mapped through `m` (IDENTITY at top level), in
 * drawing order, or `null` when a stored corner is non-finite — a frame that
 * cannot be described is dropped rather than emitting a NaN path operator.
 */
export function oleFramePlotCorners(
  entity: OleFrameEntity,
  m: Affine2D = IDENTITY,
): Point2[] | null {
  const corners = oleFrameCorners(entity);
  if (!corners) return null;
  return corners.map((c) => apply(m, { x: c.x, y: c.y }));
}

/** Top-level `OLE2FRAME` → its stroked frame rectangle (RAW world mm). */
export function oleFrameToPdfPrimitives(
  entity: OleFrameEntity,
  ctx: CadConvertContext,
): CadPrimitive[] {
  const corners = oleFramePlotCorners(entity);
  if (!corners) return [];
  return strokedPathPrimitives(entity, ctx, [
    {
      start: corners[0]!,
      segments: corners.slice(1).map((to) => ({ kind: "line" as const, to })),
      closed: true,
    },
  ]);
}
